import { useEffect, useState } from "react";
import { Check, CreditCard, ExternalLink, Loader2, Mail, Copy, TicketCheck, Clock, Bitcoin, Wallet, ShieldCheck, ShoppingCart, ChevronDown } from "lucide-react";
import Shell from "@/components/Shell";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const fmt = new Intl.NumberFormat("es-ES");

const BTC_STEPS = [
  { icon: ExternalLink, title: "1. Entra en Paybis", text: "Abre paybis.com y pulsa «Comprar / Buy Crypto». Elige pagar con tarjeta o transferencia y selecciona Bitcoin (BTC) como la moneda a comprar." },
  { icon: CreditCard, title: "2. Indica el importe", text: "Escribe los euros que quieres recargar. Paybis te mostrará cuántos BTC vas a recibir por ese importe." },
  { icon: Wallet, title: "3. Elige «cartera externa»", text: "Cuando te pregunte dónde recibir el Bitcoin, selecciona usar una cartera/billetera EXTERNA (external wallet). NO elijas la cartera interna de Paybis." },
  { icon: Copy, title: "4. Pega esta dirección", text: "En el campo de dirección de la cartera externa pega EXACTAMENTE la dirección Bitcoin de NovaIA que ves arriba. Asegúrate de que la red sea Bitcoin (BTC)." },
  { icon: ShieldCheck, title: "5. Verifica tu identidad", text: "La primera vez Paybis te pedirá verificar tu identidad (documento + selfie). Es rápido, seguro y obligatorio por ley." },
  { icon: ShoppingCart, title: "6. Confirma y paga", text: "Revisa la dirección carácter por carácter, confirma y paga. Paybis enviará el Bitcoin a esa dirección y tu saldo se recargará al confirmarse en la red." },
];

const STEPS = [
  { icon: CreditCard, title: "1. Compra el voucher", text: "Pulsa el botón de tu plan, compra el voucher Azteco en G2A y completa el pago." },
  { icon: Mail, title: "2. Revisa tu correo", text: "Nada más pagar, G2A te enviará un email de confirmación de la compra." },
  { icon: Copy, title: "3. Copia el enlace de canje", text: "Abre el correo, pulsa en «Obtener producto» y copia el enlace de canje que aparece." },
  { icon: TicketCheck, title: "4. Canjea aquí", text: "Pega ese enlace en el campo de canje de tu plan y pulsa Canjear. Tu cuenta se activa al validarlo." },
];

export default function Plans() {
  const { user, setUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [inputs, setInputs] = useState({});
  const [saving, setSaving] = useState("");
  const [redeemMsg, setRedeemMsg] = useState({});
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [btcAddress, setBtcAddress] = useState("");
  const [btcAmount, setBtcAmount] = useState("");
  const [btcBusy, setBtcBusy] = useState(false);
  const [btcMsg, setBtcMsg] = useState(null);
  const [copied, setCopied] = useState(false);
  const [btcOpen, setBtcOpen] = useState(false);

  const load = async () => {
    const [d, v] = await Promise.all([api("/plans"), api("/me/vouchers")]);
    setPlans(d.plans);
    setBtcAddress(d.btc_address || "");
    setVouchers(v.vouchers);
  };

  useEffect(() => {
    load().catch((e) => setError(formatDetail(e.message)));
  }, []);

  const copyBtc = async () => {
    try {
      await navigator.clipboard.writeText(btcAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const notifyBtc = async () => {
    setBtcBusy(true);
    setBtcMsg(null);
    try {
      await api("/me/btc-deposit", { method: "POST", body: { amount: Number(btcAmount) || 0 } });
      setBtcMsg({ type: "ok", text: "¡Aviso recibido! En cuanto confirmemos tu pago de Bitcoin en la red, sumaremos el importe a tu saldo." });
      setBtcAmount("");
    } catch (e) {
      setBtcMsg({ type: "err", text: formatDetail(e.message) });
    } finally {
      setBtcBusy(false);
    }
  };

  const redeem = async (plan_id) => {
    const url = (inputs[plan_id] || "").trim();
    setSaving(plan_id);
    setMsg("");
    setError("");
    setRedeemMsg({ ...redeemMsg, [plan_id]: { type: "load", text: "Canjeando voucher… tu dinero aparecerá en breves." } });
    try {
      await api("/me/voucher", { method: "POST", body: { plan_id, url } });
      setInputs({ ...inputs, [plan_id]: "" });
      setRedeemMsg({ ...redeemMsg, [plan_id]: { type: "ok", text: "Voucher recibido. Cuando sea aprobado, su importe se sumará a tu saldo y podrás activar la IA." } });
      await load();
    } catch (e) {
      setRedeemMsg({ ...redeemMsg, [plan_id]: { type: "err", text: formatDetail(e.message) } });
    } finally {
      setSaving("");
    }
  };

  const statusChip = (s) =>
    s === "validado"
      ? "border-teal/40 bg-teal/10 text-teal"
      : s === "rechazado"
      ? "border-danger/40 bg-danger/10 text-danger"
      : "border-lav/40 bg-lav/10 text-lav";

  return (
    <Shell>
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-teal mb-2">Depósitos y pagos</p>
      <h1 className="font-display text-2xl md:text-3xl" data-testid="plans-title">Elige tu plan y canjea tu voucher</h1>
      <p className="text-mist text-sm mt-3 max-w-2xl leading-relaxed">
        El plan define la potencia con la que la IA gestiona tu tienda. Recarga tu saldo con un
        voucher Azteco (G2A) o directamente en <strong className="text-white">Bitcoin</strong>, y actívalo. Puedes cambiar de plan cuando quieras.
      </p>

      <div className="mt-6 inline-flex items-center gap-3 rounded-2xl border border-teal/40 bg-teal/10 px-5 py-3.5" data-testid="balance-card">
        <CreditCard className="w-5 h-5 text-teal" />
        <span className="text-sm text-mist">Tu saldo disponible:</span>
        <span className="font-display text-xl text-teal">{fmt.format(user.balance || 0)} €</span>
      </div>

      <div className="mt-6" data-testid="btc-deposit-section">
        <button
          onClick={() => setBtcOpen((o) => !o)}
          data-testid="btc-toggle-btn"
          aria-expanded={btcOpen}
          className="w-full flex items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition-colors"
          style={{ borderColor: "rgba(247,147,26,0.45)", background: "rgba(247,147,26,0.08)" }}
        >
          <span className="flex items-center justify-center w-9 h-9 rounded-lg shrink-0" style={{ background: "rgba(247,147,26,0.15)", color: "#F7931A" }}>
            <Bitcoin className="w-5 h-5" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold text-white">También puedes recargar con Bitcoin</span>
            <span className="block text-xs text-mist mt-0.5">Compra BTC en Paybis y envíalo. Toca para ver el paso a paso.</span>
          </span>
          <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${btcOpen ? "rotate-180" : ""}`} style={{ color: "#F7931A" }} />
        </button>

        {btcOpen && (
          <div className="mt-3 rounded-2xl border p-5 md:p-8" data-testid="btc-deposit-body" style={{ borderColor: "rgba(247,147,26,0.4)", background: "rgba(247,147,26,0.04)" }}>
            <div className="mt-1">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist">Dirección Bitcoin de NovaIA (red BTC)</label>
              <div className="mt-2 flex flex-col sm:flex-row gap-2">
                <code
                  data-testid="btc-address"
                  className="flex-1 min-w-0 break-all rounded-xl bg-card border border-edge px-4 py-3 text-xs md:text-sm font-mono text-white"
                >
                  {btcAddress || "…"}
                </code>
                <button
                  onClick={copyBtc}
                  data-testid="btc-copy-btn"
                  className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-xs font-semibold transition-colors"
                  style={{ background: copied ? "#2DD4BF" : "#F7931A", color: "#05070C" }}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? "¡Copiada!" : "Copiar dirección"}
                </button>
              </div>
              <p className="mt-2 text-[11px] text-mist/70 flex items-start gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5 text-teal" />
                Envía SOLO Bitcoin (BTC) por la red Bitcoin a esta dirección. Enviar otra moneda u otra red hará que se pierda el dinero.
              </p>
            </div>

            <div className="mt-7">
              <h3 className="font-display text-base">Cómo comprar Bitcoin en Paybis y enviarlo, paso a paso</h3>
              <a
                href="https://paybis.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="paybis-link"
                className="mt-3 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold transition-colors"
                style={{ background: "#F7931A", color: "#05070C" }}
              >
                <ExternalLink className="w-4 h-4" />
                Ir a Paybis.com
              </a>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {BTC_STEPS.map((s) => (
                  <div key={s.title} className="rounded-xl border border-edge bg-card p-5" data-testid="btc-step">
                    <s.icon className="w-5 h-5" style={{ color: "#F7931A" }} />
                    <h4 className="font-semibold text-sm mt-3">{s.title}</h4>
                    <p className="text-mist text-xs mt-2 leading-relaxed">{s.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-7 rounded-xl border border-edge bg-card/60 p-5">
              <p className="text-sm text-white font-semibold">¿Ya has enviado el Bitcoin?</p>
              <p className="text-mist text-xs mt-1 leading-relaxed">Avísanos y, en cuanto confirmemos el pago en la red Bitcoin, sumaremos el importe a tu saldo. Indica el importe aproximado en euros (opcional).</p>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <input
                  value={btcAmount}
                  onChange={(e) => setBtcAmount(e.target.value)}
                  inputMode="decimal"
                  placeholder="Importe en € (opcional)"
                  data-testid="btc-amount-input"
                  className="flex-1 min-w-0 rounded-xl bg-card border border-edge px-3.5 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal/60"
                />
                <button
                  onClick={notifyBtc}
                  disabled={btcBusy}
                  data-testid="btc-notify-btn"
                  className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-teal text-night px-5 py-2.5 text-sm font-semibold hover:bg-tealdeep hover:text-white transition-colors disabled:opacity-60"
                >
                  {btcBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  He realizado el pago
                </button>
              </div>
              {btcMsg && (
                <p
                  data-testid="btc-notify-msg"
                  className={`mt-3 text-xs leading-relaxed rounded-lg px-3.5 py-2.5 ${
                    btcMsg.type === "ok" ? "border border-teal/40 bg-teal/10 text-teal" : "border border-danger/40 bg-danger/10 text-danger"
                  }`}
                >
                  {btcMsg.text}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-8 rounded-2xl border border-edge bg-panel p-6 md:p-8" data-testid="howto-redeem">
        <h2 className="font-display text-base mb-6">Cómo canjear tu voucher, paso a paso</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s) => (
            <div key={s.title} className="rounded-xl border border-edge bg-card p-5">
              <s.icon className="w-5 h-5 text-teal" />
              <h3 className="font-semibold text-sm mt-3">{s.title}</h3>
              <p className="text-mist text-xs mt-2 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </div>

      {msg && (
        <p className="mt-6 rounded-xl border border-teal/40 bg-teal/10 text-teal text-sm px-5 py-3.5" data-testid="redeem-success">
          {msg}
        </p>
      )}
      {error && (
        <p className="mt-6 rounded-xl border border-danger/40 bg-danger/10 text-danger text-sm px-5 py-3.5" data-testid="redeem-error">
          {error}
        </p>
      )}

      <div className="grid md:grid-cols-3 gap-6 mt-8">
        {plans.map((p) => {
          const active = user?.plan === p.id;
          return (
            <div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              className={`relative rounded-2xl border p-7 flex flex-col ${active ? "border-teal bg-card" : "border-edge bg-panel"}`}
            >
              {active && (
                <span className="absolute -top-3 left-6 rounded-full bg-teal text-night text-[11px] font-semibold px-3 py-1">
                  Tu plan actual
                </span>
              )}
              {p.popular && !active && (
                <span className="absolute -top-3 left-6 rounded-full bg-lav text-night text-[11px] font-semibold px-3 py-1">
                  Más elegido
                </span>
              )}
              <h2 className="font-display text-xl">{p.name}</h2>
              <p className="mt-4 font-display text-4xl">
                {fmt.format(p.price)} <span className="text-base text-mist font-sans">€</span>
              </p>
              <p className="text-sm text-mist mt-2">
                Ganancias estimadas:{" "}
                <span className="text-teal font-semibold">
                  {fmt.format(p.profit_min)}–{fmt.format(p.profit_max)} €/mes
                </span>
              </p>
              <ul className="mt-6 space-y-2.5 text-sm text-mist flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-teal shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>

              {p.vouchers_needed > 1 && (
                <p className="mt-5 rounded-lg border border-lav/30 bg-lav/10 text-lav text-xs px-4 py-2.5" data-testid={`plan-needs-2-${p.id}`}>
                  Este plan se activa adquiriendo <strong>{p.vouchers_needed} vouchers de 200 €</strong> (puedes canjearlos juntos o de uno en uno).
                </p>
              )}

              <a
                href={p.voucher_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`plan-buy-${p.id}`}
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl bg-teal text-night px-6 py-3 text-sm font-semibold hover:bg-tealdeep hover:text-white transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Comprar voucher {p.vouchers_needed > 1 ? `de 200 € (×${p.vouchers_needed})` : `de ${p.price} €`}
              </a>

              <label className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-mist">
                ¿Ya tienes el voucher? Canjéalo aquí
              </label>
              <div className="mt-2 flex gap-2">
                <input
                  value={inputs[p.id] || ""}
                  onChange={(e) => setInputs({ ...inputs, [p.id]: e.target.value })}
                  placeholder="Pega el enlace de canje de Azteco"
                  data-testid={`plan-voucher-input-${p.id}`}
                  className="flex-1 min-w-0 rounded-xl bg-card border border-edge px-3.5 py-2.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-teal/60"
                />
                <button
                  onClick={() => redeem(p.id)}
                  disabled={saving === p.id}
                  data-testid={`plan-redeem-${p.id}`}
                  className="shrink-0 rounded-xl bg-lav text-night px-4 py-2.5 text-xs font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {saving === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Canjear"}
                </button>
              </div>
              {redeemMsg[p.id] && (
                <p
                  data-testid={`redeem-msg-${p.id}`}
                  className={`mt-3 text-xs leading-relaxed rounded-lg px-3.5 py-2.5 ${
                    redeemMsg[p.id].type === "ok"
                      ? "border border-teal/40 bg-teal/10 text-teal"
                      : redeemMsg[p.id].type === "err"
                      ? "border border-danger/40 bg-danger/10 text-danger"
                      : "border border-lav/40 bg-lav/10 text-lav"
                  }`}
                >
                  {redeemMsg[p.id].text}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {vouchers.length > 0 && (
        <div className="mt-10 rounded-2xl border border-edge bg-panel p-6 md:p-8" data-testid="my-vouchers">
          <h2 className="font-display text-base mb-5">Tus vouchers enviados</h2>
          <div className="space-y-3">
            {vouchers.map((v) => (
              <div key={v._id} className="rounded-xl border border-edge bg-card px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 justify-between" data-testid={`my-voucher-${v._id}`}>
                <div className="min-w-0">
                  <p className="text-xs text-mist font-semibold">Plan {plans.find((p) => p.id === v.plan_id)?.name || v.plan_id}</p>
                  <a href={v.url} target="_blank" rel="noopener noreferrer" className="text-xs text-mist/70 font-mono truncate block max-w-md hover:text-teal">
                    {v.url.slice(0, 70)}{v.url.length > 70 ? "…" : ""}
                  </a>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-[10px] text-mist/50 font-mono flex items-center gap-1.5">
                    <Clock className="w-3 h-3" />
                    {new Date(v.created_at).toLocaleDateString("es-ES")}
                  </span>
                  <span className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${statusChip(v.status)}`} data-testid={`voucher-status-${v.status}`}>
                    {v.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
}
